/**
 * Export Utilities
 * Handles exporting user responses as JSON or generating print views
 */

import { getIssueResponses } from './storageUtils';

/**
 * Export issue responses as JSON
 * @param {string} issueId - The issue ID
 * @param {string} title - The issue title
 * @returns {string} JSON string
 */
export const exportAsJSON = (issueId, title) => {
  const responses = getIssueResponses(issueId);
  
  const exportData = {
    exportDate: new Date().toISOString(),
    issue: issueId,
    title: title,
    responses: Object.entries(responses.sections || {}).map(([sectionId, sectionData]) => ({
      section: sectionId,
      reflections: sectionData.reflections || {},
      ratings: sectionData.ratings || {},
      completed: sectionData.completed || false
    })),
    metadata: {
      currentSection: responses.currentSection || 0,
      isComplete: responses.isComplete || false
    }
  };
  
  return JSON.stringify(exportData, null, 2);
};

/**
 * Download JSON export as a file
 * @param {string} issueId - The issue ID
 * @param {string} title - The issue title
 */
export const downloadJSON = (issueId, title) => {
  const json = exportAsJSON(issueId, title);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = `${issueId}-${title.replace(/\s+/g, '-')}-export.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Generate HTML for printing
 * @param {string} issueId - The issue ID
 * @param {Object} issueData - The parsed issue data
 * @param {Object} responses - User responses
 * @returns {string} HTML string
 */
export const generatePrintHTML = (issueId, issueData, responses) => {
  const sections = issueData.sections || [];
  
  let html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${issueData.title} - Reality Decoder</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
      line-height: 1.6;
      color: #333;
    }
    h1 {
      color: #4f46e5;
      border-bottom: 2px solid #4f46e5;
      padding-bottom: 10px;
    }
    h2 {
      color: #3730a3;
      margin-top: 30px;
    }
    h3 {
      color: #4338ca;
      margin-top: 20px;
    }
    .section {
      margin-bottom: 30px;
      padding: 15px;
      background: #f9fafb;
      border-radius: 8px;
    }
    .reflection {
      margin: 15px 0;
      padding: 10px;
      background: #fff;
      border-left: 3px solid #4f46e5;
    }
    .reflection-label {
      font-weight: bold;
      color: #666;
      font-size: 0.9em;
    }
    .reflection-text {
      margin-top: 5px;
      white-space: pre-wrap;
    }
    .completed {
      color: #059669;
      font-weight: bold;
    }
    .rating {
      margin: 10px 0;
    }
    .rating-value {
      font-weight: bold;
      color: #4f46e5;
    }
    .tool-ref {
      display: inline-block;
      background: #e0e7ff;
      color: #4338ca;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 0.9em;
    }
    @media print {
      body { padding: 0; }
      .section { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <h1>${issueData.title}</h1>
  <p><em>Issue ID: ${issueId}</em></p>
  <p><em>Exported: ${new Date().toLocaleDateString()}</em></p>
`;

  sections.forEach((section, index) => {
    const sectionResponses = (responses.sections || {})[section.id] || {};
    const isCompleted = sectionResponses.completed;
    
    html += `
  <div class="section">
    <h2>${section.title}</h2>
    ${isCompleted ? '<p class="completed">✓ Completed</p>' : '<p>In Progress</p>'}
`;
    
    // Add content (first paragraph as preview)
    if (section.content) {
      const preview = section.content.substring(0, 300);
      html += `<p>${preview}${section.content.length > 300 ? '...' : ''}</p>`;
    }
    
    // Add reflections
    const reflections = sectionResponses.reflections || {};
    Object.entries(reflections).forEach(([key, value]) => {
      if (value) {
        html += `
    <div class="reflection">
      <div class="reflection-label">${key}:</div>
      <div class="reflection-text">${value}</div>
    </div>`;
      }
    });
    
    // Add ratings
    const ratings = sectionResponses.ratings || {};
    Object.entries(ratings).forEach(([key, value]) => {
      if (value !== undefined) {
        html += `
    <div class="rating">
      <span class="reflection-label">${key}:</span>
      <span class="rating-value">${value}/10</span>
    </div>`;
      }
    });
    
    // Add tool references
    if (section.toolReferences && section.toolReferences.length > 0) {
      html += `<p><strong>Tools referenced:</strong> `;
      section.toolReferences.forEach(tool => {
        html += `<span class="tool-ref">${tool.id}: ${tool.name}</span> `;
      });
      html += `</p>`;
    }
    
    html += `  </div>`;
  });

  html += `
</body>
</html>`;

  return html;
};

/**
 * Open print view in new window
 * @param {string} issueId - The issue ID
 * @param {Object} issueData - The parsed issue data
 * @param {Object} responses - User responses
 */
export const openPrintView = (issueId, issueData, responses) => {
  const html = generatePrintHTML(issueId, issueData, responses);
  
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
  }
};

/**
 * Export all saved issues as a single JSON file
 * @returns {string} JSON string of all data
 */
export const exportAllAsJSON = () => {
  // This would require importing storageUtils directly
  // For now, return empty structure - will be implemented with full storage integration
  return JSON.stringify({ error: 'Not implemented yet' }, null, 2);
};

export default {
  exportAsJSON,
  downloadJSON,
  generatePrintHTML,
  openPrintView,
  exportAllAsJSON
};